// 📄 components/Configuracion/CargarPlantillaModal.tsx
// Modal simple de alta: nombre del tipo + archivo XML. Vive en
// Configuración > Plantillas XML (ver ConfiguracionPlantillasXml.tsx) —
// mismo servicio (guardarPlantilla, alcance "proyecto") que usaba el botón
// "Guardar como plantilla" del Validador, ahora movido acá.
import React, { useEffect, useState } from "react";
import { X, Save, RefreshCw, AlertTriangle } from "lucide-react";
import XmlDropzone from "../shared/XmlDropzone.tsx";
import RestriccionesTagEditor from "../shared/RestriccionesTagEditor.tsx";
import PlantillaMetadatosForm from "../shared/PlantillaMetadatosForm.tsx";
import { existePlantilla, guardarPlantilla, PlantillaMetadatos } from "../../services/plantillaReferenciaService.ts";
import { construirIdPlantilla } from "../../utils/plantillaIdUtils.ts";
import { RestriccionTag } from "../../utils/xmlStructureUtils.ts";

interface CargarPlantillaModalProps {
  projectId: string;
  projectNombre?: string;
  onClose: () => void;
  onGuardado: () => void;
}

export default function CargarPlantillaModal({ projectId, projectNombre, onClose, onGuardado }: CargarPlantillaModalProps) {
  const [nombre, setNombre] = useState("");
  const [nombreArchivo, setNombreArchivo] = useState("");
  const [contenidoXML, setContenidoXML] = useState("");
  const [restricciones, setRestricciones] = useState<RestriccionTag[]>([]);
  const [metadatos, setMetadatos] = useState<PlantillaMetadatos>({});
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // El id real del documento (tipoDocumento) sale del nombre + un sufijo de
  // Document Stage si hay uno elegido, para que dos plantillas con el mismo
  // nombre pero distinto stage no se pisen (ver plantillaIdUtils.ts).
  const tipoDocumento = construirIdPlantilla(nombre, metadatos.documentStage);

  const [existeExistente, setExisteExistente] = useState<boolean | null>(null);
  const [verificandoExistencia, setVerificandoExistencia] = useState(false);

  useEffect(() => {
    if (!tipoDocumento) { setExisteExistente(null); return; }
    setVerificandoExistencia(true);
    existePlantilla(tipoDocumento, "proyecto", projectId)
      .then(setExisteExistente)
      .catch((err) => {
        console.error("Error verificando plantilla existente:", err);
        setExisteExistente(null);
      })
      .finally(() => setVerificandoExistencia(false));
  }, [tipoDocumento, projectId]);

  const handleGuardar = async () => {
    setError(null);
    if (!nombre.trim()) { setError("Escribe un nombre para el tipo de documento."); return; }
    if (!contenidoXML) { setError("Carga un archivo XML."); return; }
    if (existeExistente) {
      setError("Ya existe una plantilla con este nombre y Document Stage. Cambia el nombre o selecciona un stage diferente.");
      return;
    }

    setGuardando(true);
    try {
      await guardarPlantilla(tipoDocumento, nombreArchivo, contenidoXML, "proyecto", projectId, nombre, restricciones, metadatos);
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar la plantilla.");
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">Cargar plantilla</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre del tipo de documento</label>
            <input
              type="text"
              className={inputClass}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="ej. Aviso de Recepción"
            />
          </div>

          <div>
            <label className={labelClass}>Archivo XML</label>
            <XmlDropzone
              accent="indigo"
              nombreArchivo={nombreArchivo}
              contenidoArchivo={contenidoXML}
              onArchivoCargado={(n, c) => { setNombreArchivo(n); setContenidoXML(c); }}
              onError={(msg) => setError(msg)}
            />
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Metadatos de trazabilidad (opcional)</h4>
            <PlantillaMetadatosForm valores={metadatos} onChange={setMetadatos} />
          </div>

          {contenidoXML && (
            <div className="pt-2 border-t border-white/10">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Restricciones por campo (opcional)</h4>
              <RestriccionesTagEditor contenidoXML={contenidoXML} restricciones={restricciones} onChange={setRestricciones} />
            </div>
          )}

          <p className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg p-3">
            Se guardará solo para <span className="font-medium text-gray-200">{projectNombre || "este proyecto"}</span> — visible para todos sus miembros, con prioridad sobre la plantilla global del mismo tipo.
          </p>

          {verificandoExistencia && (
            <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Verificando si ya existe una plantilla para este tipo...</p>
          )}

          {existeExistente && !verificandoExistencia && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Ya existe una plantilla con este nombre y Document Stage. Cambia el nombre o selecciona un stage diferente.</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">Cancelar</button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={guardando || verificandoExistencia || !nombre.trim() || !contenidoXML || !!existeExistente}
              className="flex items-center gap-2 disabled:bg-gray-600 bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
